import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import StatusBadge from "@/components/common/StatusBadge";
import { toast } from "sonner";
import { Loader2, ArrowUpCircle, ArrowDownCircle, X, TrendingUp, TrendingDown, FileText, Download, ChevronLeft, ChevronRight } from "lucide-react";

function buildReportHtml(r) {
  const fmtNum = (n) => Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const period = `${r.period.start === "all time" ? "All Time" : r.period.start} — ${r.period.end === "now" ? "Present" : r.period.end}`;
  const generated = new Date().toLocaleString();
  const actionRows = ["bet", "win", "topup", "refund", "rollback", "adjust"]
    .filter(a => r.counts[a] > 0)
    .map(a => `<tr><td style="text-transform:uppercase;font-weight:600">${a}</td><td style="text-align:right">${r.counts[a]}</td><td style="text-align:right;color:${r.summary[a] < 0 ? "#e74c3c" : "#27ae60"}">${r.summary[a] < 0 ? "" : "+"}${fmtNum(r.summary[a])} ${r.currency}</td></tr>`)
    .join("");
  const topGamesRows = r.top_games.length > 0
    ? r.top_games.map((g, i) => `<tr><td>${i + 1}</td><td>${g.game}</td><td style="text-align:right">${fmtNum(g.bet_volume)} ${r.currency}</td></tr>`).join("")
    : `<tr><td colspan="3" style="text-align:center;color:#888">No game data available</td></tr>`;
  const txnRows = r.transactions.map(t =>
    `<tr><td style="white-space:nowrap">${new Date(t.created_at).toLocaleString()}</td><td style="text-transform:uppercase;font-weight:600">${t.action}</td><td style="text-align:right;color:${t.amount < 0 ? "#e74c3c" : "#27ae60"};font-family:monospace">${t.amount < 0 ? "" : "+"}${fmtNum(t.amount)}</td><td style="text-align:right;font-family:monospace">${fmtNum(t.balance_after)}</td><td style="font-size:11px;color:#666">${t.reference || "—"}</td></tr>`
  ).join("");
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Credit Report - ${r.operator_name}</title>
<style>
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;color:#1a1a2e;padding:40px;max-width:1100px;margin:0 auto;font-size:13px;background:#fff}
h1{font-size:22px;font-weight:700;margin-bottom:4px}
h2{font-size:14px;font-weight:700;text-transform:uppercase;letter-spacing:0.1em;color:#555;margin:28px 0 12px;border-bottom:2px solid #e0e0e0;padding-bottom:6px}
.header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:24px;border-bottom:3px solid #1a1a2e;padding-bottom:16px}
.header-right{text-align:right;font-size:11px;color:#666}
.meta{font-size:12px;color:#666;margin-top:4px}
.stats{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin:16px 0}
.stat{border:1px solid #e0e0e0;border-radius:6px;padding:12px;text-align:center}
.stat-label{font-size:10px;text-transform:uppercase;letter-spacing:0.1em;color:#888;margin-bottom:4px}
.stat-val{font-size:20px;font-weight:700;font-family:monospace}
.stat-val.red{color:#e74c3c}.stat-val.green{color:#27ae60}
table{width:100%;border-collapse:collapse;margin:8px 0}
th,td{padding:6px 10px;text-align:left;border-bottom:1px solid #eee;font-size:12px}
th{background:#f8f8f8;font-weight:600;text-transform:uppercase;letter-spacing:0.05em;font-size:10px;color:#666}
.footer{margin-top:32px;padding-top:12px;border-top:2px solid #e0e0e0;font-size:10px;color:#999;text-align:center}
@media print{body{padding:20px}@page{margin:15mm;size:A4}}
.print-btn{background:#1a1a2e;color:#fff;border:none;padding:8px 20px;border-radius:4px;cursor:pointer;font-size:12px;font-weight:600}
.print-btn:hover{background:#2a2a4e}
@media print{.no-print{display:none!important}}
</style></head><body>
<div class="no-print" style="text-align:right;margin-bottom:16px">
  <button class="print-btn" onclick="window.print()">Print / Save as PDF</button>
</div>
<div class="header">
  <div>
    <div style="font-size:10px;text-transform:uppercase;letter-spacing:0.15em;color:#888;margin-bottom:4px">CoreBridge Nexus iGaming Aggregator</div>
    <h1>Operator Credit Report</h1>
    <div class="meta">${r.operator_name} · ${r.operator_id}</div>
  </div>
  <div class="header-right">
    <div>Period: <strong>${period}</strong></div>
    <div>Currency: <strong>${r.currency}</strong></div>
    <div>Generated: ${generated}</div>
    <div>Transactions: ${r.total_transactions}</div>
  </div>
</div>

<div style="background:#fff8e1;border:1px solid #ffe082;border-radius:6px;padding:10px 14px;font-size:11px;color:#6d5600;margin-bottom:12px">
  <strong>Credit Model:</strong> Operator credit is deducted for every player bet placed through the aggregator. Player wins do not return credit to the operator float. The only way to increase credit is via manual top-up through CoreBridge.
</div>

<div class="stats">
  <div class="stat"><div class="stat-label">Current Balance</div><div class="stat-val">${fmtNum(r.current_balance)}</div></div>
  <div class="stat"><div class="stat-label">Total Bet Costs</div><div class="stat-val red">-${fmtNum(r.total_debit)}</div></div>
  <div class="stat"><div class="stat-label">Total Top-ups / Refunds</div><div class="stat-val green">+${fmtNum(r.total_credit)}</div></div>
  <div class="stat"><div class="stat-label">Net Movement</div><div class="stat-val ${r.net_movement < 0 ? "red" : "green"}">${r.net_movement < 0 ? "" : "+"}${fmtNum(r.net_movement)}</div></div>
</div>

<h2>Breakdown by Action</h2>
<table><thead><tr><th>Action</th><th style="text-align:right">Count</th><th style="text-align:right">Total Amount</th></tr></thead><tbody>${actionRows}</tbody></table>

<h2>Top Games by Bet Volume</h2>
<table><thead><tr><th>#</th><th>Game</th><th style="text-align:right">Bet Volume</th></tr></thead><tbody>${topGamesRows}</tbody></table>

<h2>Transaction Detail (${r.total_transactions} records)</h2>
<table><thead><tr><th>Date/Time</th><th>Action</th><th style="text-align:right">Amount</th><th style="text-align:right">Balance After</th><th>Reference</th></tr></thead><tbody>${txnRows}</tbody></table>

<div class="footer">
  This report is auto-generated by CoreBridge Nexus Aggregator for accounting and reconciliation purposes.<br/>
  Report ID: RPT-${r.operator_id}-${Date.now().toString(36).toUpperCase()} · CoreBridge Technologies
</div>
</body></html>`;
}

export default function OperatorCredits() {
  const [operators, setOperators] = useState([]);
  const [selected, setSelected] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [actionFilter, setActionFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [ledgerBusy, setLedgerBusy] = useState(false);
  const [modal, setModal] = useState(null);
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [reportModal, setReportModal] = useState(false);
  const [reportStart, setReportStart] = useState("");
  const [reportEnd, setReportEnd] = useState("");
  const [reportBusy, setReportBusy] = useState(false);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 10;

  const loadOperators = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/api/admin/operator-credits");
      setOperators(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const loadLedger = async (opId, action = "") => {
    setLedgerBusy(true);
    try {
      const params = new URLSearchParams();
      if (action) params.set("action", action);
      params.set("limit", "300");
      const { data } = await api.get(`/api/admin/operator-credits/${opId}/ledger?${params}`);
      setLedger(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLedgerBusy(false);
  };

  useEffect(() => { loadOperators(); }, []);

  const selectOperator = (op) => {
    setSelected(op);
    setActionFilter("");
    setLedger([]);
    setPage(0);
    loadLedger(op.operator_id);
  };

  useEffect(() => {
    if (selected) { setPage(0); loadLedger(selected.operator_id, actionFilter); }
  }, [actionFilter]);

  const handleSubmit = async () => {
    if (!amount || parseFloat(amount) <= 0) { toast.error("Enter a valid amount"); return; }
    setSubmitting(true);
    try {
      const endpoint = modal === "topup" ? "topup" : "deduct";
      const { data } = await api.post(`/api/admin/operator-credits/${selected.operator_id}/${endpoint}`, {
        amount: parseFloat(amount), reference,
      });
      toast.success(`${modal === "topup" ? "Top-up" : "Deduction"} successful. New balance: ${data.balance.toFixed(2)}`);
      setModal(null); setAmount(""); setReference("");
      setSelected({ ...selected, balance: data.balance });
      setOperators(ops => ops.map(o => o.operator_id === selected.operator_id ? { ...o, balance: data.balance } : o));
      loadLedger(selected.operator_id, actionFilter);
    } catch (e) { toast.error(formatApiError(e)); }
    setSubmitting(false);
  };

  const openReport = async () => {
    setReportBusy(true);
    try {
      const params = new URLSearchParams();
      if (reportStart) params.set("start", reportStart + "T00:00:00Z");
      if (reportEnd) params.set("end", reportEnd + "T23:59:59Z");
      const { data } = await api.get(`/api/admin/operator-credits/${selected.operator_id}/report?${params}`);
      const html = buildReportHtml(data);
      const w = window.open("", "_blank");
      w.document.write(html);
      w.document.close();
      setReportModal(false);
    } catch (e) { toast.error(formatApiError(e)); }
    setReportBusy(false);
  };

  const downloadCsv = async () => {
    setReportBusy(true);
    try {
      const params = new URLSearchParams();
      if (reportStart) params.set("start", reportStart + "T00:00:00Z");
      if (reportEnd) params.set("end", reportEnd + "T23:59:59Z");
      const res = await api.get(`/api/admin/operator-credits/${selected.operator_id}/report/csv?${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `credit_report_${selected.operator_id}_${reportStart || "all"}_${reportEnd || "now"}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      setReportModal(false);
    } catch (e) { toast.error(formatApiError(e)); }
    setReportBusy(false);
  };

  const actionColor = (action) => {
    const map = { bet: "text-red-400", win: "text-emerald-400", topup: "text-blue-400", refund: "text-amber-400", adjust: "text-purple-400", rollback: "text-orange-400" };
    return map[action] || "text-[var(--text-2)]";
  };

  return (
    <div className="space-y-6" data-testid="operator-credits-page">
      <div className="text-[var(--text-2)] text-sm">Operator prepaid credit float. View balances, transaction history, and manage top-ups or deductions.</div>

      {/* Operator balance cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {busy && <div className="col-span-full text-center py-8"><Loader2 className="inline animate-spin" size={18}/></div>}
        {!busy && operators.map(op => (
          <button
            key={op.operator_id}
            onClick={() => selectOperator(op)}
            className={`card-surface p-4 text-left transition-all hover:border-[var(--gold)] ${selected?.operator_id === op.operator_id ? "border-[var(--gold)] ring-1 ring-[var(--gold)]" : ""}`}
            data-testid={`op-card-${op.operator_id}`}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold truncate">{op.operator_name}</span>
              <StatusBadge status={op.status}/>
            </div>
            <div className="text-2xl font-mono font-bold tracking-tight">
              {op.balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              <span className="text-xs text-[var(--text-3)] ml-1">{op.currency}</span>
            </div>
            <div className="text-[10px] font-mono text-[var(--text-3)] mt-1">{op.operator_id}</div>
          </button>
        ))}
      </div>

      {/* Selected operator detail */}
      {selected && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-[var(--text-3)]">Credit Ledger</div>
              <div className="text-lg font-semibold">{selected.operator_name}</div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setReportModal(true)}
                className="btn-ghost px-4 py-2 text-sm flex items-center gap-2 border border-[var(--border)]"
                data-testid="btn-report"
              >
                <FileText size={15}/> Generate Report
              </button>
              <button
                onClick={() => setModal("topup")}
                className="btn-primary px-4 py-2 text-sm flex items-center gap-2"
                data-testid="btn-topup"
              >
                <ArrowUpCircle size={15}/> Top Up
              </button>
              <button
                onClick={() => setModal("deduct")}
                className="btn-ghost px-4 py-2 text-sm flex items-center gap-2 border border-[var(--border)]"
                data-testid="btn-deduct"
              >
                <ArrowDownCircle size={15}/> Deduct
              </button>
            </div>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="card-surface p-3">
              <div className="text-[10px] uppercase tracking-widest text-[var(--text-3)]">Current Balance</div>
              <div className="font-mono text-xl font-bold mt-1">{selected.balance.toLocaleString(undefined, { minimumFractionDigits: 2 })} <span className="text-xs text-[var(--text-3)]">{selected.currency}</span></div>
            </div>
            <div className="card-surface p-3">
              <div className="text-[10px] uppercase tracking-widest text-[var(--text-3)]">Total Bet Costs</div>
              <div className="font-mono text-xl font-bold mt-1 text-red-400">
                {ledger.filter(l => l.amount < 0).reduce((s, l) => s + Math.abs(l.amount), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>
            </div>
            <div className="card-surface p-3">
              <div className="text-[10px] uppercase tracking-widest text-[var(--text-3)]">Top-ups / Refunds</div>
              <div className="font-mono text-xl font-bold mt-1 text-emerald-400">
                {ledger.filter(l => l.amount > 0).reduce((s, l) => s + l.amount, 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>
            </div>
            <div className="card-surface p-3">
              <div className="text-[10px] uppercase tracking-widest text-[var(--text-3)]">Transactions</div>
              <div className="font-mono text-xl font-bold mt-1">{ledger.length}</div>
            </div>
          </div>

          {/* Filter bar */}
          <div className="flex gap-3">
            <select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              className="px-3 py-2 bg-[var(--surface)] border border-[var(--border)] rounded text-sm"
              data-testid="credit-action-filter"
            >
              <option value="">All actions</option>
              {["bet", "topup", "refund", "rollback", "adjust"].map(a => <option key={a} value={a}>{a.toUpperCase()}</option>)}
            </select>
          </div>

          {/* Ledger table */}
          <div className="card-surface overflow-hidden">
            <div className="overflow-x-auto max-h-[60vh]">
              <table className="aggr-table">
                <thead>
                  <tr>
                    <th>Action</th>
                    <th>Amount</th>
                    <th>Balance After</th>
                    <th>Reference</th>
                    <th>Idempotency Key</th>
                    <th>When</th>
                  </tr>
                </thead>
                <tbody>
                  {ledgerBusy && <tr><td colSpan={6} className="text-center py-6"><Loader2 className="inline animate-spin" size={14}/></td></tr>}
                  {!ledgerBusy && ledger.length === 0 && (
                    <tr><td colSpan={6} className="text-center py-8 text-[var(--text-3)]">No transactions yet</td></tr>
                  )}
                  {!ledgerBusy && ledger.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map((entry, i) => (
                    <tr key={entry.id || i}>
                      <td>
                        <span className={`inline-flex items-center gap-1.5 text-xs font-semibold uppercase ${actionColor(entry.action)}`}>
                          {entry.amount < 0 ? <TrendingDown size={13}/> : <TrendingUp size={13}/>}
                          {entry.action}
                        </span>
                      </td>
                      <td className={`font-mono font-semibold ${entry.amount < 0 ? "text-red-400" : "text-emerald-400"}`}>
                        {entry.amount < 0 ? "" : "+"}{entry.amount.toFixed(2)}
                        <span className="text-[var(--text-3)] text-xs ml-1">{entry.currency}</span>
                      </td>
                      <td className="font-mono text-sm">{entry.balance_after.toFixed(2)}</td>
                      <td className="text-xs text-[var(--text-2)] max-w-[200px] truncate" title={entry.reference}>{entry.reference || "—"}</td>
                      <td className="font-mono text-xs text-[var(--text-3)] max-w-[180px] truncate" title={entry.idempotency_key}>{entry.idempotency_key}</td>
                      <td className="text-xs text-[var(--text-3)] whitespace-nowrap">{new Date(entry.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {ledger.length > PAGE_SIZE && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-[var(--border)]">
                <div className="text-xs text-[var(--text-3)]">
                  Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, ledger.length)} of {ledger.length}
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPage(p => p - 1)} disabled={page === 0}
                    className="px-2 py-1 rounded border border-[var(--border)] text-xs disabled:opacity-30 hover:bg-[var(--surface)]">
                    <ChevronLeft size={14}/>
                  </button>
                  <span className="text-xs text-[var(--text-2)]">Page {page + 1} of {Math.ceil(ledger.length / PAGE_SIZE)}</span>
                  <button onClick={() => setPage(p => p + 1)} disabled={(page + 1) * PAGE_SIZE >= ledger.length}
                    className="px-2 py-1 rounded border border-[var(--border)] text-xs disabled:opacity-30 hover:bg-[var(--surface)]">
                    <ChevronRight size={14}/>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Report Modal */}
      {reportModal && selected && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setReportModal(false)}>
          <div className="card-surface w-full max-w-md p-6" onClick={e => e.stopPropagation()} data-testid="report-modal">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-[var(--text-3)]">{selected.operator_name}</div>
                <div className="text-xl font-semibold">Generate Credit Report</div>
              </div>
              <button onClick={() => setReportModal(false)} className="text-[var(--text-3)] hover:text-[var(--text)]"><X size={18}/></button>
            </div>
            <div className="text-sm text-[var(--text-2)] mb-4">
              Select a date range to generate a detailed credit activity report. Leave blank for all-time data.
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-widest text-[var(--text-3)] block mb-1">Start Date</label>
                  <input type="date" value={reportStart} onChange={e => setReportStart(e.target.value)}
                    className="w-full px-3 py-2 bg-[var(--surface)] border border-[var(--border)] rounded text-sm" data-testid="report-start"/>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-[var(--text-3)] block mb-1">End Date</label>
                  <input type="date" value={reportEnd} onChange={e => setReportEnd(e.target.value)}
                    className="w-full px-3 py-2 bg-[var(--surface)] border border-[var(--border)] rounded text-sm" data-testid="report-end"/>
                </div>
              </div>
              <button onClick={openReport} disabled={reportBusy}
                className="w-full py-2.5 rounded text-sm font-semibold flex items-center justify-center gap-2 btn-primary disabled:opacity-50" data-testid="report-view">
                {reportBusy ? <Loader2 className="animate-spin" size={14}/> : <FileText size={14}/>}
                View Printable Report
              </button>
              <button onClick={downloadCsv} disabled={reportBusy}
                className="w-full py-2.5 rounded text-sm font-semibold flex items-center justify-center gap-2 btn-ghost border border-[var(--border)] disabled:opacity-50" data-testid="report-csv">
                {reportBusy ? <Loader2 className="animate-spin" size={14}/> : <Download size={14}/>}
                Download CSV
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Topup / Deduct Modal */}
      {modal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => { setModal(null); setAmount(""); setReference(""); }}>
          <div className="card-surface w-full max-w-md p-6" onClick={e => e.stopPropagation()} data-testid="credit-modal">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-[var(--text-3)]">{selected.operator_name}</div>
                <div className="text-xl font-semibold">{modal === "topup" ? "Top Up Credit" : "Deduct Credit"}</div>
              </div>
              <button onClick={() => { setModal(null); setAmount(""); setReference(""); }} className="text-[var(--text-3)] hover:text-[var(--text)]"><X size={18}/></button>
            </div>
            <div className="text-sm text-[var(--text-2)] mb-4">
              Current balance: <span className="font-mono font-semibold text-[var(--text)]">{selected.balance.toFixed(2)} {selected.currency}</span>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-widest text-[var(--text-3)] block mb-1">Amount ({selected.currency})</label>
                <input
                  type="number" min="0.01" step="0.01" value={amount} onChange={e => setAmount(e.target.value)}
                  placeholder="0.00" autoFocus
                  className="w-full px-3 py-2 bg-[var(--surface)] border border-[var(--border)] rounded text-lg font-mono"
                  data-testid="credit-amount"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[var(--text-3)] block mb-1">Reference / Note</label>
                <input
                  type="text" value={reference} onChange={e => setReference(e.target.value)}
                  placeholder="e.g. Monthly top-up, correction, etc."
                  className="w-full px-3 py-2 bg-[var(--surface)] border border-[var(--border)] rounded text-sm"
                  data-testid="credit-reference"
                />
              </div>
              <button
                onClick={handleSubmit} disabled={submitting || !amount}
                className={`w-full py-2.5 rounded text-sm font-semibold flex items-center justify-center gap-2 ${
                  modal === "topup" ? "btn-primary" : "bg-red-600 hover:bg-red-700 text-white"
                } disabled:opacity-50`}
                data-testid="credit-submit"
              >
                {submitting && <Loader2 className="animate-spin" size={14}/>}
                {modal === "topup" ? "Confirm Top Up" : "Confirm Deduction"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
